import React, {useMemo} from 'react';


import {useAlertFrequencyTypes} from 'src/hooks/dictionaries/useAlertDictionaries';
import {NoResultPage} from 'src/modules/entitlements';
import {AlertHeader} from 'src/modules/profile/user/tabs/content/alerts/headers/alert-header';
import {FrequencyHeader} from 'src/modules/profile/user/tabs/content/alerts/headers/frequency-header';
import {AlertItemTypeHeader} from 'src/modules/profile/user/tabs/content/alerts/headers/item-type-header';
import {AlertListTypeHeader} from 'src/modules/profile/user/tabs/content/alerts/headers/list-type-header';
import {SavedAlertTableRow} from 'src/modules/profile/user/tabs/content/alerts/saved-alert-table-row';
import {SIZES} from 'src/modules/profile/user/tabs/content/constants';
import {useGetSearchesEntityIdsCounts} from 'src/modules/profile/user/tabs/content/hooks/useGetSearchesEntityIdsCounts';
import {AlertPageRequest} from 'src/openapi/admin/models/AlertPageRequest';
import {GeneralAlert} from 'src/openapi/admin/models/GeneralAlert';

import {Group} from '@pb/kit-component-group';
import {PaginationStripe} from '@pb/kit-component-pagination';
import Table, {Body, Cell, Head, Row} from '@pb/kit-component-table';

import {useGetUserAlertContentAvailableOptions} from './hooks/useGetUserAlertContentAvailableOptions';


type Props = {
    userId: number,
    isNoAlerts: boolean,
    total: number,
    data: GeneralAlert[],
    setPage: (page: number) => void,
    setPageSize: (size: number) => void,
    criteria: AlertPageRequest,
};

export const SavedAlertTable = (props: Props) => {
    const {userId, isNoAlerts, total, data, criteria, setPage, setPageSize} = props;
    const {getAvailableOptionsByCode} = useGetUserAlertContentAvailableOptions({userId});

    const savedSearchIds = useMemo(() => data.map(alert => alert.contentId), [data]);

    const {
        data: savedSearchesCountsData,
        isLoading: savedSearchesCountsLoading,
    } = useGetSearchesEntityIdsCounts({
        userId,
        ids: savedSearchIds,
    });
    const {getAlertFrequencyTypeByCode} = useAlertFrequencyTypes();

    const getAlertCount = (contentId: string) => {
        return savedSearchesCountsData?.find(item => item.savedSearchId === contentId)?.alertCount || 0;
    };

    return (
        <>
            <Group gap="m" direction="v">
                <Table fixed={true} align="middle">
                    <Head>
                        <AlertHeader/>
                        <AlertItemTypeHeader/>
                        <AlertListTypeHeader getItemDescription={getAvailableOptionsByCode}/>
                        <Cell>Alert Type</Cell>
                        <FrequencyHeader/>
                        <Cell># of Alerts</Cell>
                    </Head>

                    <Body>
                        {total > 0 ?
                                data.map((item: GeneralAlert, i: number) => (
                                    <SavedAlertTableRow
                                        key={item.contentId + i}
                                        contentId={item.contentId}
                                        frequencyDescription={
                                            getAlertFrequencyTypeByCode(
                                                item.alertFrequency)?.description
                                                || item.alertFrequency
                                        }
                                        searchTypeDescription={getAvailableOptionsByCode(item.searchListType)}
                                        alertCount={getAlertCount(item.contentId)}
                                        showZebra={i % 2 === 0}
                                        userId={userId}
                                        alert={item}
                                        isAlertCountLoading={savedSearchesCountsLoading}
                                    />
                                )) : (
                                    <Row>
                                        <Cell colSpan={6} className="text-align_center">
                                            <div style={{
                                                width: '100%',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                padding: '20px 0',
                                            }}>
                                                <NoResultPage
                                                    title="No data to show"
                                                    description={isNoAlerts
                                                        ? 'This user has not created any alerts yet.'
                                                        : 'No data found for the selected criteria.'}
                                                />
                                            </div>
                                        </Cell>
                                    </Row>
                                )}
                    </Body>
                </Table>
                <PaginationStripe
                    totalCount={total}
                    currentPage={criteria?.pagination?.currentPage || 1}
                    pageSize={criteria?.pagination?.pageSize || 7}
                    onPaginationChange={p => setPage(p.currentPage)}
                    showSizeSwitcher={true}
                    goToPage={true}
                    sizeSwitcherPosition="RIGHT"
                    sizes={SIZES}
                    onChangePaginationSize={(_p, size) => setPageSize(size)}
                />
            </Group>
        </>
    );
};
